import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth, API } from "@/AuthContext";
import LangToggle from "@/components/LangToggle";
import ThemeToggle from "@/components/ThemeToggle";
import { ArrowLeft, Users, FileText, TrendingUp, ShieldCheck, UserCheck, Activity, Loader2, Mail, Search, Megaphone, Clock } from "lucide-react";

export default function Admin() {
  const { user, loading: authLoading, logout, theme } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/me`)
      .then((r) => {
        setAuthorized(!!r.data.is_admin);
        if (!r.data.is_admin) navigate("/dashboard", { replace: true });
      })
      .catch(() => { setAuthorized(false); navigate("/dashboard", { replace: true }); });
  }, [user, navigate]);

  useEffect(() => {
    if (!authorized) return;
    Promise.all([
      axios.get(`${API}/admin/stats`),
      axios.get(`${API}/admin/users`),
      axios.get(`${API}/admin/activity`),
    ]).then(([s, u, a]) => {
      setStats(s.data); setUsers(u.data.users || []); setActivity(a.data.series || []);
    }).catch(() => {}).finally(() => setLoadingData(false));
  }, [authorized]);

  if (authLoading || authorized === null) {
    return <div className="min-h-screen flex items-center justify-center bg-bone"><Loader2 className="w-5 h-5 animate-spin text-pine" /></div>;
  }
  if (!authorized) return null;

  const filteredUsers = users.filter((u) => {
    if (!q) return true;
    const s = `${u.name || ""} ${u.email || ""} ${u.practice_name || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const kpis = stats ? [
    { label: "Berichte gesamt", value: stats.total_reports ?? stats.total_sessions, icon: FileText, accent: "pine" },
    { label: "Therapeut:innen", value: stats.total_users, icon: Users, accent: "pine" },
    { label: "Aktiv (24h)", value: stats.active_users_last_24h ?? 0, icon: Clock, accent: "terracotta" },
    { label: "Patient:innen", value: stats.total_patients, icon: UserCheck, accent: "pine" },
    { label: "Neu (7T)", value: stats.new_users_last_7_days, icon: TrendingUp, accent: "terracotta" },
    { label: "Aktiv (7T)", value: stats.active_users_last_7_days, icon: Activity, accent: "terracotta" },
  ] : [];

  return (
    <div className={`min-h-screen bg-bone ${theme === "dark" ? "dark" : ""}`} data-testid="admin-page">
      <header className="h-16 bg-surface border-b border-hairline flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4" /> Therapeuten-Ansicht
          </Link>
          <div className="h-5 w-px bg-hairline" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-terracotta flex items-center justify-center text-bone">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div className="font-heading text-lg tracking-tight">Admin</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/communication"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover transition"
            data-testid="admin-comm-link"
          >
            <Megaphone className="w-4 h-4" /> Kommunikation
          </Link>
          <ThemeToggle />
          <LangToggle />
          <button onClick={logout} className="text-sm text-ink-muted hover:text-ink" data-testid="admin-logout">Abmelden</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-terracotta/10 text-terracotta text-[10px] uppercase tracking-[0.2em] font-semibold">
            <ShieldCheck className="w-3 h-3" /> Admin-Bereich
          </div>
          <h1 className="mt-3 font-heading text-4xl tracking-tight">Übersicht & Aktivität</h1>
          <p className="mt-2 text-ink-muted text-sm">Eingeloggt als <span className="text-pine font-medium">{user.email}</span></p>
        </motion.div>

        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {kpis.map((k, i) => (
              <motion.div
                key={k.label}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="bg-surface border border-hairline rounded-2xl p-5"
                data-testid={`admin-kpi-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold leading-tight">{k.label}</div>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${k.accent === "terracotta" ? "bg-terracotta/10 text-terracotta" : "bg-pine-50 text-pine"}`}>
                    <k.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </div>
                </div>
                <div className="mt-3 font-heading text-3xl tracking-tight">{k.value}</div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Activity Chart */}
        {activity.length > 0 && <ActivityChart data={activity} />}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Name, E-Mail oder Praxis suchen..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-surface border border-hairline focus:outline-none focus:border-pine text-sm"
            data-testid="admin-search"
          />
        </div>

        {/* Users table */}
        <div className="bg-surface border border-hairline rounded-2xl overflow-hidden" data-testid="admin-users-table">
          <div className="px-5 py-4 border-b border-hairline flex items-center justify-between">
            <h3 className="font-heading text-lg">Alle Therapeut:innen</h3>
            <div className="text-xs text-ink-muted">{filteredUsers.length} / {users.length}</div>
          </div>

          {loadingData ? (
            <div className="py-16 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-pine" /></div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-16 text-center text-ink-muted text-sm">Keine Nutzer gefunden.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-pine-50/40 text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
                    <th className="text-left px-5 py-3">Therapeut:in</th>
                    <th className="text-left px-3 py-3 hidden md:table-cell">Praxis</th>
                    <th className="text-right px-3 py-3">Patienten</th>
                    <th className="text-right px-3 py-3">Berichte</th>
                    <th className="text-left px-3 py-3 hidden lg:table-cell">Letzter Bericht</th>
                    <th className="text-left px-3 py-3 hidden lg:table-cell">Registriert</th>
                    <th className="text-right px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} className="hover:bg-pine-50/30 transition" data-testid={`admin-user-${u.user_id}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-pine-50 text-pine flex items-center justify-center font-heading text-sm">{u.name?.[0]}</div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium truncate flex items-center gap-2">
                              {u.name}
                              {u.is_admin && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-terracotta/10 text-terracotta text-[9px] uppercase tracking-wider font-bold">Admin</span>
                              )}
                            </div>
                            <div className="text-xs text-ink-muted truncate">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 hidden md:table-cell">
                        <div className="text-ink">{u.practice_name || <span className="text-ink-muted italic">—</span>}</div>
                        {u.practice_phone && <div className="text-xs text-ink-muted">{u.practice_phone}</div>}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-pine font-semibold">{u.patients}</td>
                      <td className="px-3 py-3 text-right">
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-md bg-pine-50 text-pine font-mono font-bold">{u.sessions}</span>
                      </td>
                      <td className="px-3 py-3 text-xs text-ink-muted hidden lg:table-cell whitespace-nowrap">
                        {u.last_session_at ? new Date(u.last_session_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-3 text-xs text-ink-muted hidden lg:table-cell whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <a
                          href={`mailto:${u.email}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs border border-hairline hover:bg-pine-50 hover:text-pine transition"
                          data-testid={`admin-mail-${u.user_id}`}
                        >
                          <Mail className="w-3 h-3" /> Mail
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="bg-surface border border-hairline rounded-2xl p-6" data-testid="admin-activity-chart">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-pine">Berichte · letzte 7 Tage</div>
          <div className="mt-1 text-sm text-ink-muted">Anzahl erstellter SOAP-Berichte pro Tag</div>
        </div>
        <div className="text-2xl font-heading">{data.reduce((s, d) => s + d.count, 0)}</div>
      </div>
      <div className="mt-6 flex items-end gap-3 h-32">
        {data.map((d) => {
          const h = (d.count / max) * 100;
          const isToday = d.date === today;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-2" data-testid={`activity-bar-${d.date}`}>
              <div className="text-xs font-mono text-ink font-semibold">{d.count}</div>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(h, 4)}%` }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className={`w-full rounded-t-lg ${isToday ? "bg-pine" : "bg-pine-50"} ${d.count === 0 ? "opacity-40" : ""}`}
                style={{ minHeight: "4px" }}
              />
              <div className={`text-[10px] uppercase tracking-wider ${isToday ? "text-pine font-semibold" : "text-ink-muted"}`}>
                {new Date(d.date).toLocaleDateString("de-DE", { weekday: "short" })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
